/**
 * The graph query box: Obsidian's search field rather than a bare input.
 *
 * As you type it offers the query operators (tag:, path:, file:, -, OR) and,
 * once a field is started, live completions drawn from the vault itself: real
 * tags, real folder paths, real note titles. Selecting a completion replaces
 * only the token under the caret, so a half-built compound query survives.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Input } from "@/ui/Input";
import type { GraphData } from "./types";
import s from "./Graph.module.css";

export interface QuerySource {
  tags: string[];
  folders: string[];
  files: string[];
}

/** Completion pools from the FULL graph, so a filtered-out value still offers. */
export function sourceFrom(full: GraphData): QuerySource {
  const tags = new Set<string>();
  const folders = new Set<string>();
  const files = new Set<string>();
  for (const n of full.nodes) {
    for (const t of n.tags) tags.add(t);
    if (n.kind !== "note") continue;
    files.add(n.label);
    // every ancestor prefix of a note's path is a suggestible folder
    const path = n.path ?? "";
    let idx = path.indexOf("/");
    while (idx > 0) {
      folders.add(path.slice(0, idx));
      idx = path.indexOf("/", idx + 1);
    }
  }
  const alpha = (a: string, b: string) => a.localeCompare(b);
  return { tags: [...tags].sort(alpha), folders: [...folders].sort(alpha), files: [...files].sort(alpha) };
}

export interface SuggestItem {
  label: string;
  insert: string;
  hint?: string;
}

const MAX_ITEMS = 8;
const FIELD_TOKEN_RE = /^(-?)(tag|path|file):(.*)$/i;
const OPERATORS: SuggestItem[] = [
  { label: "tag:", insert: "tag:", hint: "match a tag" },
  { label: "path:", insert: "path:", hint: "match a folder" },
  { label: "file:", insert: "file:", hint: "match a title" },
  { label: "-", insert: "-", hint: "exclude" },
  { label: "OR", insert: "OR ", hint: "either side" },
];

function quoteValue(v: string): string {
  // the parser reads phrases with double quotes; strip any embedded ones
  return /[\s()"]/.test(v) ? `"${v.replaceAll('"', "")}"` : v;
}

/**
 * Suggestions for the token under the caret. Pure, so the token-boundary and
 * field-completion rules are testable without a DOM.
 */
export function suggestionsFor(
  value: string,
  caret: number,
  src: QuerySource,
): { items: SuggestItem[]; start: number } {
  // token boundary: unquoted whitespace or paren before the caret. Quote
  // parity is tracked because a phrase in progress swallows spaces.
  let start = 0;
  let inQuote = false;
  for (let i = 0; i < caret; i++) {
    const c = value[i];
    if (c === '"') inQuote = !inQuote;
    else if (!inQuote && (c === " " || c === "\t" || c === "(" || c === ")")) start = i + 1;
  }
  const token = value.slice(start, caret);
  const lower = token.toLowerCase();

  // an empty token offers nothing: a dropdown on bare focus would sit over
  // the controls below the input and intercept their clicks
  if (!token) return { items: [], start };

  const field = FIELD_TOKEN_RE.exec(token);
  if (field) {
    const neg = field[1] ?? "";
    const name = (field[2] ?? "").toLowerCase();
    const part = (field[3] ?? "").replaceAll('"', "").toLowerCase();
    const pool = name === "tag" ? src.tags : name === "path" ? src.folders : src.files;
    const items = pool
      .filter((v) => v.toLowerCase().includes(part))
      .slice(0, MAX_ITEMS)
      .map((v) => ({ label: v, insert: `${neg}${name}:${quoteValue(v)}` }));
    return { items, start };
  }

  // bare token: operators first, then real tags and titles it could mean
  const ops = OPERATORS.filter((o) => o.label.toLowerCase().startsWith(lower));
  const tagHits = src.tags
    .filter((t) => t.toLowerCase().includes(lower))
    .slice(0, 3)
    .map((t) => ({ label: `#${t}`, insert: `tag:${quoteValue(t)}`, hint: "tag" }));
  const fileHits = src.files
    .filter((f) => f.toLowerCase().includes(lower))
    .slice(0, 4)
    .map((f) => ({ label: f, insert: `file:${quoteValue(f)}`, hint: "note" }));
  return { items: [...ops, ...tagHits, ...fileHits].slice(0, MAX_ITEMS), start };
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  source: QuerySource;
  ariaLabel: string;
  placeholder?: string;
  className?: string;
}

export function QueryInput({ value, onChange, source, ariaLabel, placeholder, className }: Props) {
  const ref = useRef<HTMLInputElement | null>(null);
  const [open, setOpen] = useState(false);
  /** -1 means nothing highlighted, so Enter never hijacks a finished query. */
  const [active, setActive] = useState(-1);
  const [caret, setCaret] = useState(0);

  const { items, start } = useMemo(
    () => (open ? suggestionsFor(value, Math.min(caret, value.length), source) : { items: [], start: 0 }),
    [open, value, caret, source],
  );
  useEffect(() => {
    if (active >= items.length) setActive(-1);
  }, [items.length, active]);

  const syncCaret = () => setCaret(ref.current?.selectionStart ?? value.length);

  const apply = (item: SuggestItem) => {
    const end = Math.min(caret, value.length);
    const next = value.slice(0, start) + item.insert + value.slice(end);
    onChange(next);
    const pos = start + item.insert.length;
    // stays open on purpose: picking "tag:" should immediately offer values
    setActive(-1);
    requestAnimationFrame(() => {
      ref.current?.focus();
      ref.current?.setSelectionRange(pos, pos);
      setCaret(pos);
    });
  };

  return (
    // className lands on the wrapper: the inner input is width 100%, so row
    // layouts (the group rows) size the wrapper, not the input
    <div className={[s.qwrap, className ?? ""].join(" ")}>
      <Input
        ref={ref}
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel}
        role="combobox"
        aria-expanded={open && items.length > 0}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => {
          onChange(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          setOpen(true);
          setActive(-1);
        }}
        onFocus={() => {
          setOpen(true);
          syncCaret();
        }}
        onBlur={() => setOpen(false)}
        onClick={syncCaret}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            setOpen(false);
            return;
          }
          if (!open || items.length === 0) {
            if (e.key === "ArrowDown") setOpen(true);
            return;
          }
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((a) => (a + 1) % items.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((a) => (a <= 0 ? items.length - 1 : a - 1));
          } else if (e.key === "Enter" && active >= 0) {
            e.preventDefault();
            const item = items[active];
            if (item) apply(item);
          } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
            // caret moved: recompute the token on the next tick
            requestAnimationFrame(syncCaret);
          }
        }}
      />
      {open && items.length > 0 ? (
        <ul className={s.suggest} role="listbox" data-graph-suggest>
          {items.map((it, i) => (
            <li
              key={`${it.insert}|${it.label}`}
              role="option"
              aria-selected={i === active}
              className={s.suggestItem}
              data-active={i === active || undefined}
              // mousedown, not click: click fires after blur already closed us
              onMouseDown={(e) => {
                e.preventDefault();
                apply(it);
              }}
              onMouseEnter={() => setActive(i)}
            >
              <span className={s.suggestLabel}>{it.label}</span>
              {it.hint ? <span className={s.suggestHint}>{it.hint}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
